import classNames from 'classnames'
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { Body2, Caption1, Title2 } from '@ui/Typography/Typography'
import { Input, InputProps } from '@ui/components/Input/Input'

import { useWindowResize } from '@utils/domUtils'

import { Box } from '@shared/components/Box'

import styles from './style.module.scss'

interface FieldGroupFieldDef {
  label?: string
  field?: InputProps
}

export interface FieldGroup {
  sectionLabel: string
  fields: FieldGroupFieldDef[]
}

export interface ModalBodyFieldGroupsProps {
  sections: FieldGroup[]
}

interface RowMetrics {
  top: number
  height: number
}

export const ModalBodyFieldGroups: React.FC<ModalBodyFieldGroupsProps> = (props) => {
  const isMobile = useWindowResize()

  return isMobile ? (
    <ModalBodyFieldGroupsMobile {...props} />
  ) : (
    <ModalBodyFieldGroupsDesktop {...props} />
  )
}

const ModalBodyFieldGroupsDesktop: React.FC<ModalBodyFieldGroupsProps> = ({ sections }) => {
  const [firstSection] = sections ?? []
  const showHeaderLabels = labelsMatch(sections)
  const columnCount = firstSection?.fields.length ?? 0

  const containerRef = useRef<HTMLDivElement>(null)
  const headerRowRef = useRef<HTMLDivElement | null>(null)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])

  const [rowMetrics, setRowMetrics] = useState<RowMetrics[]>([])

  const measure = useCallback(() => {
    const container = containerRef.current

    if (!container) {
      return
    }

    const containerTop = container.getBoundingClientRect().top

    const next = sections.map((_, idx) => {
      const el = rowRefs.current[idx]

      if (!el) {
        return { top: 0, height: 0 }
      }

      const rect = el.getBoundingClientRect()

      return { top: rect.top - containerTop, height: rect.height }
    })

    setRowMetrics(next)
  }, [sections])

  useLayoutEffect(() => {
    measure()

    const container = containerRef.current

    if (!container) {
      return
    }

    const observer = new ResizeObserver(() => measure())
    observer.observe(container)

    return () => observer.disconnect()
  }, [measure])

  rowRefs.current = []

  return (
    <Box ref={containerRef} flex align='flex-end' gap='x10' fullWidth position='relative'>
      <Box position='relative' flexItem='0 0 101px' alignSelf='stretch'>
        {sections.map((section, idx) => (
          <Box
            flex
            align='center'
            position='absolute'
            key={section.sectionLabel}
            className={classNames({
              [styles.whiteSpacePre]: section.fields?.length === 1,
            })}
            style={
              rowMetrics[idx]
                ? { top: rowMetrics[idx].top, height: rowMetrics[idx].height }
                : undefined
            }
          >
            <Caption1>{section.sectionLabel}</Caption1>
          </Box>
        ))}
      </Box>

      <div
        className={styles.fieldGroupsGrid}
        style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
      >
        {showHeaderLabels
          ? firstSection?.fields.map((field, idx) => (
              <Box key={field.label} textAlign='center' ref={idx === 0 ? headerRowRef : undefined}>
                <Body2>{field.label}</Body2>
              </Box>
            ))
          : firstSection?.fields.map((_, idx) => <div key={idx} />)}

        {sections.map((section, sectionIdx) =>
          section.fields?.map((field, idx) => (
            <div
              key={section.sectionLabel + (field.label || idx)}
              ref={
                idx === 0
                  ? (el) => {
                      rowRefs.current[sectionIdx] = el
                    }
                  : undefined
              }
            >
              <FieldGroupField {...field} label={showHeaderLabels ? undefined : field.label} />
            </div>
          ))
        )}
      </div>
    </Box>
  )
}

const ModalBodyFieldGroupsMobile: React.FC<ModalBodyFieldGroupsProps> = ({ sections }) => {
  return (
    <Box flex direction='column' gap='x24' px='x16' fullWidth flexItem='1'>
      {sections.map((section) => (
        <Box flex direction='column' gap='x16' key={section.sectionLabel}>
          <Title2>{section.sectionLabel}</Title2>

          <div className={styles.fieldGroupFields}>
            {section.fields.map((field, idx) => (
              <Box flex direction='column' gap='x4' key={field.label ?? idx}>
                <Body2>{field.label}</Body2>
                <Input {...field.field} />
              </Box>
            ))}
          </div>
        </Box>
      ))}
    </Box>
  )
}

const FieldGroupField: React.FC<FieldGroupFieldDef> = (props) => {
  const { label, field } = props

  return (
    <Box flex direction='column' align='flex-start' gap='x10'>
      {label && <Caption1>{label}</Caption1>}
      <Input {...field} />
    </Box>
  )
}

const labelsMatch = (sections: FieldGroup[]): boolean => {
  if (sections.length === 0) {
    return true
  }

  const baseline = sections[0].fields.map((field) => field.label)

  return sections.every((section) => {
    const labels = section.fields.map((field) => field.label)

    return (
      labels.length === baseline.length && labels.every((label, idx) => label === baseline[idx])
    )
  })
}
